import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { UserCircle, Info, Volume2, VolumeX, Loader2, FileText, Download } from "lucide-react";
import { FaRegCopy, FaCheck, FaRobot, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint } from "react-icons/fa";
import { generateDownloadUrl } from '../../services/api/KaiConfigData';
import { Tooltip } from "react-tooltip";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TypingText from '../TypingText';
import { MessageListProps } from '../../types/types';

import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";
import { getSafeImageSrc } from '../../utils/imageUtils';
import { textToSpeech } from '../../services/api/KaiConfigData';

declare global {
  interface Window {
    MathJax?: {
      typeset?: (elements?: Array<HTMLElement>) => void;
      typesetPromise?: (elements?: Array<HTMLElement>) => Promise<any>;
      startup?: {
        promise?: Promise<any>;
      };
      tex?: any;
      svg?: any;
      options?: any;
      [key: string]: any;
    }
  }
}

interface V1MessageListProps extends MessageListProps {
  themeOverride?: 'light' | 'dark' | 'auto';
}

// TypingText (the typewriter) only renders a tiny markdown subset (bold/italic/
// h3/links/newlines). Messages containing tables, lists, code blocks, or block
// quotes briefly look broken during the typewriter pass before ReactMarkdown
// takes over. This helper detects those cases so we can skip the typewriter
// entirely and render with ReactMarkdown from the start. Conservative by
// design — false positives (treating prose as complex) just lose the typing
// animation; false negatives would let the visual glitch reappear.
const COMPLEX_MARKDOWN_RE = new RegExp(
  [
    // Markdown table separator row: | --- | :---: | ---: | etc.
    String.raw`\n\s*\|[\s:|-]*-+[\s:|-]*\|`,
    // Bullet list at start of a line (-, *, +)
    String.raw`(^|\n)\s*[-*+]\s+\S`,
    // Numbered list at start of a line
    String.raw`(^|\n)\s*\d+\.\s+\S`,
    // Fenced code block
    String.raw`\n\s*\x60{3}`,
    // Block quote
    String.raw`(^|\n)>\s`,
  ].join("|"),
);
const hasComplexMarkdown = (text: string | undefined | null): boolean => {
  if (!text || text.length < 8) return false;
  return COMPLEX_MARKDOWN_RE.test(text);
};

// Match TypingText's link behavior: open in new tab, inherit text color.
const markdownComponents = {
  a: ({ node, ...props }: any) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'inherit', textDecoration: 'underline' }}
    />
  ),
};

const MessageList: React.FC<V1MessageListProps> = ({
  messages,
  configData,
  activeChatId,
  isNewChat,
  typingChatId,
  hasTyped,
  loading,
  isSidebarOpen,
  onScrollBottom,
  onChunkReference,
  messagesEndRef,
  loadingIndicatorRef,
  safeAiIconSrc,
  themeOverride = 'auto'
}) => {
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [loadingTTSMessageId, setLoadingTTSMessageId] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { app_id } = useParams();
  const netId = localStorage.getItem('net_id') || '';

  // Text-to-Speech function using Azure
  const speakMessage = async (text: string, messageId: string | undefined) => {
    // Stop any ongoing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    // If clicking on the same message that's speaking, just stop
    if (speakingMessageId === messageId) {
      setSpeakingMessageId(null);
      return;
    }

    // Clean the text (remove markdown and HTML)
    const cleanText = text
      .replace(/<[^>]+>/g, '') // Remove HTML tags
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers
      .replace(/###\s*/g, '') // Remove heading markers
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Convert links to just text
      .replace(/\n+/g, '. ') // Replace newlines with periods
      .trim();

    if (!cleanText) return;

    try {
      setLoadingTTSMessageId(messageId || null);

      // Call Azure TTS API
      const audioData = await textToSpeech(cleanText, netId, app_id || '');

      // Create audio blob and play
      const audioBlob = new Blob([audioData], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setLoadingTTSMessageId(null);
        setSpeakingMessageId(messageId || null);
      };

      audio.onended = () => {
        setSpeakingMessageId(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setLoadingTTSMessageId(null);
        setSpeakingMessageId(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
        console.error('Error playing audio');
      };

      await audio.play();

    } catch (error) {
      console.error('Error in text-to-speech:', error);
      setLoadingTTSMessageId(null);
      setSpeakingMessageId(null);
      alert('Failed to convert text to speech. Please try again.');
    }
  };

  // Get file-type-specific icon based on file extension
  const getFileIcon = (fileName: string, size: number = 14) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const iconStyle = { flexShrink: 0, width: size, height: size };
    switch (ext) {
      case 'pdf':
        return <FaFilePdf style={{ ...iconStyle, color: '#e74c3c' }} />;
      case 'doc':
      case 'docx':
        return <FaFileWord style={{ ...iconStyle, color: '#2b579a' }} />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel style={{ ...iconStyle, color: '#217346' }} />;
      case 'ppt':
      case 'pptx':
        return <FaFilePowerpoint style={{ ...iconStyle, color: '#d24726' }} />;
      default:
        return <FileText size={size} style={{ flexShrink: 0 }} />;
    }
  };

  // Download uploaded file using SAS token
  const handleFileDownload = async (blobUrl: string, fileName: string) => {
    try {
      setDownloadingFileId(blobUrl);
      const sasUrl = await generateDownloadUrl(blobUrl);
      const link = document.createElement('a');
      link.href = sasUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Use configData colors as default with theme override
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const configIsLight = isLightColor(backgroundColor);
  
  // Determine effective theme based on themeOverride
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';
  
  // Theme-aware colors
  const lighterBackground = themeOverride === 'auto'
    ? (configIsLight ? darkenColor(backgroundColor, 5) : lightenColor(backgroundColor, 5))
    : (effectiveIsLight ? '#f7f7f8' : '#2d2d30');

  const darkerTabBackground = themeOverride === 'auto'
    ? (configIsLight ? darkenColor(backgroundColor, 15) : lightenColor(backgroundColor, 15))
    : (effectiveIsLight ? '#e5e5e5' : '#404040');

  const themeTextColor = themeOverride === 'auto'
    ? configData.app_ui_settings.ui_fontcolor
    : (effectiveIsLight ? '#000000' : '#ffffff');

  const iconColor = themeOverride === 'auto'
  ? configData.app_ui_settings.ui_iconcolor
  : effectiveIsLight
    ? 'black'
    : 'white';

  useEffect(() => {
  const updateSafeSource = async (): Promise<void> => {
    const baseSrc = configData.app_ai_settings.ai_face; // e.g., 'face.svg'

    let themeSuffix = '';
    if (themeOverride === 'auto') {
      themeSuffix = ''; // use default icon
    } else if (effectiveIsLight) {
      themeSuffix = '-light';
    } else {
      themeSuffix = '-dark';
    }

    // Compose new src, e.g. 'face-light.svg' or 'face-dark.svg'
    const srcWithTheme = baseSrc.replace(/(\.svg|\.png|\.jpg)$/, `${themeSuffix}$1`);

    const safeSrc = await getSafeImageSrc(srcWithTheme);
    // setSafeAiIconSrc(safeSrc);
  };
  updateSafeSource();
}, [configData.app_ai_settings.ai_face, themeOverride, effectiveIsLight]);

  // console.log("iconColor"+iconColor);

  const handleTypingFinished = (isTyping: boolean) => {
    setTimeout(() => {
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise()
          .catch((err: any) => console.error('MathJax error in parent:', err));
      } else if (window.MathJax?.typeset) {
        window.MathJax.typeset();
      }
    }, 0);
  };

  useEffect(() => {
    if (hasTyped && typeof window !== 'undefined' && window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise()
        .catch(err => console.error("MathJax render error:", err));
    }
  }, [hasTyped]);

  // When we bypass the typewriter for a complex-markdown message (see
  // hasComplexMarkdown() below), handleTypingFinished never fires — so the
  // hasTyped-driven MathJax trigger above misses this message. Re-run MathJax
  // explicitly when a bypassed complex message arrives at the tail.
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    const bypassedTypewriter =
      lastMsg?.sender === 'assistant'
      && isNewChat
      && typingChatId === activeChatId
      && hasComplexMarkdown(lastMsg?.message);
    if (!bypassedTypewriter) return;
    const t = setTimeout(() => {
      if (typeof window === 'undefined') return;
      if (window.MathJax?.typesetPromise) {
        window.MathJax.typesetPromise().catch(() => {});
      } else if (window.MathJax?.typeset) {
        window.MathJax.typeset();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [messages.length, isNewChat, typingChatId, activeChatId]);

  return (
    <div className="space-y-6">
      {messages.map((msg, index) => {
        const isLastAssistantMessage =
          index === messages.length - 1 && msg.sender === 'assistant';

        return (
          <div
            key={index}
            className="w-full"
          >
            {msg.sender === 'user' ? (
              // User message - Right aligned like ChatGPT
              <div className="flex justify-end">
                <div className="flex items-start gap-3 max-w-[70%]">
                  <div
                    className="group p-3 rounded-2xl"
                    style={{
                      backgroundColor: lighterBackground,
                      color: themeTextColor,
                      wordWrap: 'break-word',
                    }}
                    onClick={() => {
                      if (window.innerWidth <= 1024) {
                        setActiveMessageId(prev => (prev === msg.message_id ? null : msg.message_id!));
                      }
                    }}
                  >
                    {msg.message && (
                      <div className="markdown-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {msg.message}
                        </ReactMarkdown>
                      </div>
                    )}
                    {/* File attachments with download */}
                    {msg.fileAttachments && msg.fileAttachments.length > 0 && (
                      <div className={`flex flex-wrap gap-1.5 ${msg.message ? 'mt-2 pt-2' : ''}`} style={msg.message ? { borderTop: `1px solid ${effectiveIsLight ? '#e5e7eb' : '#4b5563'}` } : {}}>
                        {msg.fileAttachments.map((file, fIdx) => (
                          <button
                            key={fIdx}
                            onClick={() => file.blobUrl && handleFileDownload(file.blobUrl, file.name)}
                            disabled={!file.blobUrl || downloadingFileId === file.blobUrl}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors"
                            style={{
                              backgroundColor: effectiveIsLight ? '#e5e7eb' : '#374151',
                              color: themeTextColor,
                              cursor: file.blobUrl ? 'pointer' : 'default',
                              opacity: file.blobUrl ? 1 : 0.6,
                            }}
                            title={file.blobUrl ? `Download ${file.name}` : file.name}
                          >
                            {getFileIcon(file.name, 14)}
                            <span className="truncate" style={{ maxWidth: '120px' }}>{file.name}</span>
                            {file.blobUrl && (
                              downloadingFileId === file.blobUrl ? (
                                <Loader2 size={12} className="animate-spin" style={{ flexShrink: 0 }} />
                              ) : (
                                <Download size={12} style={{ flexShrink: 0 }} />
                              )
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <UserCircle className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: themeTextColor }} />
                </div>
              </div>
            ) : (
              // Assistant message - Left aligned like ChatGPT
              <div className="flex justify-start">
                <div className="flex items-start gap-3 max-w-[70%]">
                  {safeAiIconSrc ? (
                    <img
                      src={safeAiIconSrc}
                      alt="AI Face"
                      className="h-6 w-6 flex-shrink-0 object-contain"
                      style={{
                        color: iconColor,
                        minWidth: '1.5rem',
                        minHeight: '1.5rem',
                      }}
                    />
                  ) : (
                    <FaRobot
                      className="h-6 w-6 mt-1 flex-shrink-0"
                      style={{ color: iconColor }}
                    />
                  )}
                  <div
                    className="group flex flex-col"
                    style={{
                      color: themeTextColor,
                      wordWrap: 'break-word',
                    }}
                    onClick={() => {
                      if (window.innerWidth <= 1024) {
                        setActiveMessageId(prev => (prev === msg.message_id ? null : msg.message_id!));
                      }
                    }}
                  >
                    <div className="prose prose-sm max-w-none">
                      {isLastAssistantMessage && isNewChat && typingChatId === activeChatId && !hasTyped && !hasComplexMarkdown(msg.message) ? (
                        <TypingText
                          text={msg.message}
                          speed={3}
                          onTypingFinished={handleTypingFinished}
                          onTypingUpdate={onScrollBottom}
                        />
                      ) : (
                        <div className="markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {msg.message}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>

                    <div className={`
                      flex justify-start mt-2 -ml-1
                      ${window.innerWidth <= 1024
                        ? activeMessageId === msg?.message_id
                          ? 'flex'
                          : 'hidden'
                        : 'hidden group-hover:flex'}
                    `}>
                      {/* Text-to-Speech Button (Azure) */}
                                            <span
                                              data-tooltip-id={`speak_msg_${index}`}
                                              className={`flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors ${
                                                loadingTTSMessageId === msg.message_id ? 'cursor-wait' : 'cursor-pointer'
                                              }`}
                                              style={{ backgroundColor: 'transparent' }}
                                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                              onClick={() => !loadingTTSMessageId && speakMessage(msg.message, msg.message_id)}
                                            >
                                              {loadingTTSMessageId === msg.message_id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" style={{ color: themeTextColor }} />
                                              ) : speakingMessageId === msg.message_id ? (
                                                <VolumeX className="h-4 w-4" style={{ color: themeTextColor }} />
                                              ) : (
                                                <Volume2 className="h-4 w-4" style={{ color: themeTextColor }} />
                                              )}
                                            </span>
                                            <Tooltip
                                              id={`speak_msg_${index}`}
                                              place="bottom"
                                              style={{
                                                color: themeTextColor,
                                                backgroundColor: lighterBackground
                                              }}
                                            >
                                              {loadingTTSMessageId === msg.message_id
                                                ? 'Loading...'
                                                : speakingMessageId === msg.message_id
                                                ? 'Stop'
                                                : 'Read aloud (Azure)'}
                                            </Tooltip>
                      {copiedMessage === msg.message ? (
                        <span 
                          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors"
                          style={{ backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <FaCheck className="h-4 w-4" title="Copied" style={{ color: themeTextColor }} />
                        </span>
                      ) : (
                        <>
                          <span  
                            data-tooltip-id={`copy_msg_${index}`}
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors cursor-pointer"
                            style={{ backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                          >
                            <FaRegCopy
                              className="h-4 w-4"
                              style={{ color: themeTextColor }}
                              onClick={() => {
                                const plainText = msg.message.replace(/<[^>]+>/g, '');
                                navigator.clipboard.writeText(plainText);
                                setCopiedMessage(msg.message);
                                setTimeout(() => setCopiedMessage(null), 2000);
                              }}
                            />
                          </span>
                          <Tooltip 
                            id={`copy_msg_${index}`} 
                            place="bottom" 
                            style={{ 
                              color: themeTextColor,
                              backgroundColor: lighterBackground
                            }}
                          >
                            Copy
                          </Tooltip>
                        </>
                      )}
                      {msg.isRefAvailable && (
                        <>
                          <span  
                            data-tooltip-id={`reference_chunk_${index}`}
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-opacity-80 transition-colors cursor-pointer"
                            style={{ backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerTabBackground)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                          >
                            <Info
                              className="h-4 w-4"
                              style={{ color: themeTextColor }}
                              onClick={() => {
                                if (msg?.message_id) {
                                  onChunkReference(msg.message_id);
                                }
                              }}
                            />
                          </span>
                          <Tooltip 
                            id={`reference_chunk_${index}`}
                            place="bottom" 
                            style={{ 
                              color: themeTextColor,
                              backgroundColor: lighterBackground
                            }}
                          >
                            Reference
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div ref={messagesEndRef} />

      {loading && (
        <div className="flex justify-start">
          <div className="flex items-start gap-3">
            {safeAiIconSrc ? (
              <img
                src={safeAiIconSrc}
                alt="AI Face"
                className="h-6 w-6 mt-1 flex-shrink-0 object-contain"
                style={{ color: configData.app_ui_settings.ui_iconcolor }}
              />
            ) : (
<FaRobot
                className="h-6 w-6 mt-1 flex-shrink-0"
                style={{ color: themeTextColor }}
              />
            )}
            <div 
              ref={loadingIndicatorRef} 
              className="flex items-center text-sm font-medium"
              style={{ color: themeTextColor }}
            >
              Thinking<span className="dot-typing"></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;